/** Metre-scale tablelands inside the existing regional heightfield footprint.
 * Heights are continuous, single-valued, and independent of mesh ownership.
 * The small edge fields move the cliff in plan; they never multiply cap height. */
import { planarField } from './landforms.js';

export interface PlateauProfileSource {
  readonly widthM:number;
  readonly halfLengthM:number;
  readonly reliefM:number;
  readonly floorStartM:number;
  readonly floorEndM:number;
  readonly seed:number;
}
export interface PlateauProfileSample {
  /** Absolute regional height, including the original valley's floor. */
  heightM:number;
  /** Quiet, low-grade top; does not include cliffs or lower benches. */
  cap:number;
  /** Subordinate talus and lower benches. */
  apron:number;
  /** Normalized exposed landform mass, for the existing mountain intent. */
  mass:number;
  /** Escarpment proximity for conservative mesh refinement. */
  face:number;
  /** Distance to the nearest escarpment band in descriptor metres. */
  faceDistanceM:number;
}
export const plateauProfileSample=():PlateauProfileSample=>({heightM:0,cap:0,apron:0,mass:0,face:0,faceDistanceM:Infinity});
const clamp=(v:number):number=>Math.max(0,Math.min(1,v));
const smooth=(v:number):number=>{const t=clamp(v);return t*t*(3-2*t);};
const mix=(a:number,b:number,t:number):number=>a+(b-a)*t;
function corner(k:number,seed:number):number {
  let h=seed^Math.imul(k,374761393);
  h=Math.imul(h^(h>>>13),1274126177);h^=h>>>16;
  return (h>>>0)/2147483648-1;
}
/** Linear interpolation gives coherent angular setbacks, with no staircase
 * discontinuities at lattice boundaries and no per-sample allocations. */
function edgeField(uM:number,spanM:number,seed:number):number {
  const x=uM/spanM,k=Math.floor(x);return mix(corner(k,seed),corner(k+1,seed),x-k);
}
/** Almost planar face, with short rounded joins instead of a long sigmoid. */
function faceRamp(v:number):number {
  const t=clamp(v),e=.10;
  if(t<e)return t*t/(2*e*(1-e));
  if(t>1-e)return 1-(1-t)*(1-t)/(2*e*(1-e));
  return (t-e*.5)/(1-e);
}
/** A moving break between two large planes makes a splayed lower buttress or
 * recessed upper face. Its elevation changes along the cliff, so the break
 * does not turn into a continuous horizontal terrace around the table. */
function brokenFace(v:number,buttress:number,detail:number):number {
  const t=faceRamp(v),kneeT=.30+.40*buttress,kneeH=.50+.17*detail-.12*buttress;
  return t<kneeT?t*kneeH/kneeT:kneeH+(t-kneeT)*(1-kneeH)/(1-kneeT);
}
function apronRamp(v:number,bench:number):number {
  const t=clamp(v),graded=t*t*(2-t);
  // Only one partial bench per flank. Different seeded longitudinal masks
  // prevent concentric terrace rings, while all sections stay monotone.
  const stepped=t<.40?t*.55/.40:t<.72?.55+(t-.40)*(.09/.32):.64+(t-.72)*(.36/.28);
  return mix(graded,stepped,bench);
}

/** Sample the positive flank, measured from its winding valley centre.
 * Reuse `out`; this helper performs no allocation or transcendental calls.
 * The cap spans about 1.5 widths. Faces vary from steep 70 m recesses to
 * 220 m splayed buttresses, scaled down on small worlds. End approaches spread
 * the rise over half the descriptor length instead of closing in a cliff. */
export function samplePlateauProfile(f:PlateauProfileSource,uM:number,acrossFromValleyM:number,out:PlateauProfileSample):PlateauProfileSample {
  const width=f.widthM,s=uM/f.halfLengthM;
  const floor=mix(f.floorStartM,f.floorEndM,(s+1)*.5);
  out.heightM=floor;out.cap=out.apron=out.mass=out.face=0;out.faceDistanceM=Infinity;
  const x=acrossFromValleyM;

  const edgeScale=Math.min(1,width/800);
  const innerDetail=edgeField(uM,260,f.seed^0x494e4e52);
  const outerDetail=edgeField(uM+97,330,f.seed^0x4f555452);
  const innerTop=.94*width+edgeScale*(58*innerDetail+26*edgeField(uM,670,f.seed^0x42555454));
  const outerTop=2.48*width+edgeScale*(54*outerDetail+24*edgeField(uM,790,f.seed^0x43415053));
  // The lower edge has its own, larger-scale erosion pattern. Moving both
  // edges together would simply extrude the brow into full-height vertical
  // ribs; these independent widths and knees make large diagonal rock planes.
  const innerButtress=clamp(.5+.85*edgeField(uM+113,460,f.seed^0x544f4549));
  const outerButtress=clamp(.5+.85*edgeField(uM-71,530,f.seed^0x544f454f));
  const innerFace=Math.min(width*.35,72+154*innerButtress);
  const outerFace=Math.min(width*.35,78+150*outerButtress);
  const innerToe=innerTop-innerFace,outerToe=outerTop+outerFace;
  const acrossDistance=Math.min(Math.max(innerToe-x,0,x-innerTop),Math.max(outerTop-x,0,x-outerToe));
  const alongDistance=Math.max(0,Math.abs(uM)-f.halfLengthM);
  out.faceDistanceM=Math.sqrt(acrossDistance*acrossDistance+alongDistance*alongDistance);
  if(x<=.38*width||x>=3.4*width||Math.abs(s)>=1)return out;
  const innerBench=smooth((innerButtress-.27)/.62)*.90;
  const outerBench=smooth((outerButtress-.35)/.58)*.90;
  const innerApron=.23+.17*innerButtress+.035*innerDetail;
  const outerApron=.27+.16*outerButtress+.035*outerDetail;
  let shape:number;
  if(x<innerToe) {
    shape=innerApron*apronRamp((x-.38*width)/(innerToe-.38*width),innerBench);
    out.apron=smooth((x-.38*width)/(.12*width));
  } else if(x<innerTop) {
    const t=(x-innerToe)/innerFace;
    shape=mix(innerApron,1,brokenFace(t,innerButtress,innerDetail));
    out.face=1;
  } else if(x<=outerTop) {
    shape=1;
    out.cap=smooth((x-innerTop)/24)*smooth((outerTop-x)/24);
  } else if(x<outerToe) {
    const t=(outerToe-x)/outerFace;
    shape=mix(outerApron,1,brokenFace(t,outerButtress,outerDetail));
    out.face=1;
  } else {
    shape=outerApron*apronRamp((3.4*width-x)/(3.4*width-outerToe),outerBench);
    out.apron=smooth((3.4*width-x)/(.16*width));
  }
  // Face intent extends across the rounded joins, keeping conservative LOD
  // queries continuous even when their sample lands just above the brow.
  out.face=Math.max(smooth((x-innerToe+24)/24)*(1-smooth((x-innerTop)/24)),
    smooth((x-outerTop+24)/24)*(1-smooth((x-outerToe)/24)));
  out.apron=x<innerTop?smooth((x-.38*width)/(.12*width))*(1-smooth((x-innerToe)/innerFace)):
    smooth((3.4*width-x)/(.16*width))*smooth((x-outerTop)/outerFace);
  const approach=smooth((1-Math.abs(s))/.52);
  const middle=(f.floorStartM+f.floorEndM)*.5;
  const nativeGrade=(f.floorEndM-f.floorStartM)/(2*f.halfLengthM);
  const tableGrade=Math.max(-.015,Math.min(.015,nativeGrade));
  const crossGrade=.004+.003*corner(0,f.seed^0x54494c54);
  // A small additive cap field retains facets without making the entire
  // 400 m relief pulse by 20 percent. The shared plane ignores crest knots.
  const capRise=Math.min(f.reliefM*.44,f.halfLengthM*.12);
  const table=middle+capRise+uM*tableGrade+(x-1.65*width)*crossGrade
    +2*planarField(uM/620,x/540,.37,f.seed^0x5441424c);
  const rise=Math.max(0,table-floor)*approach;
  out.heightM=floor+rise*shape;
  out.cap*=approach;
  out.apron*=approach;
  out.face*=approach;
  out.mass=clamp(rise*shape/f.reliefM);
  return out;
}
